/**
 * Ensures a user exists in the Airtable users table.
 * Creates user if not found. Used by SignUpPage, SignInPage, CheckOAuthGoogle.
 */

import { extractDomain } from '../helpers.js';
import {
  clientTypeGrantsPaidAccess,
  companyGrantsPaidAccess,
  userNeedsAutoPaid,
} from '../utils/paidClientAccess.js';

const LOG = (...args) => console.log('[ensureUserInAirtable]', ...args);

const apiKey = process.env.REACT_APP_AIR_KEY;
const baseId = process.env.REACT_APP_AIR_BASEID;
const usersTableId = process.env.REACT_APP_AIR_USERS;

const PERSONAL_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.ca', 'yahoo.co.uk',
  'outlook.com', 'hotmail.com', 'hotmail.ca', 'live.com', 'msn.com',
  'icloud.com', 'me.com', 'aol.com', 'protonmail.com', 'proton.me',
  'mail.com', 'zoho.com',
];

const isPersonalEmail = (email) => {
  if (!email) return true;
  const domain = extractDomain(email)?.toLowerCase();
  return PERSONAL_EMAIL_DOMAINS.includes(domain);
};

async function fetchWithRetry(url, options, retries = 3) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const res = await fetch(url, options);
    if (res.status === 429 && attempt < retries) {
      const delay = Math.min(2000 * Math.pow(2, attempt), 30000);
      LOG('429 rate limit, retrying in', delay, 'ms');
      await new Promise((r) => setTimeout(r, delay));
      continue;
    }
    return res;
  }
}

/** Create a user record in Airtable. Returns { id } or throws. */
async function createAirtableUser(fields) {
  const url = `https://api.airtable.com/v0/${baseId}/${usersTableId}`;
  LOG('POST create, fields:', JSON.stringify(fields));
  const res = await fetchWithRetry(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fields }),
  });
  const text = await res.text();
  if (!res.ok) {
    LOG('Create failed:', res.status, text);
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  const data = JSON.parse(text);
  LOG('Create success, id:', data.id);
  return data;
}

async function markUserPaid(userId, existingPaid, markPaid) {
  if (!userId || !markPaid || !userNeedsAutoPaid(existingPaid)) return;
  const url = `https://api.airtable.com/v0/${baseId}/${usersTableId}`;
  const res = await fetchWithRetry(url, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ records: [{ id: userId, fields: { Paid: 'paid' } }] }),
  });
  if (!res.ok) {
    LOG('Paid update failed:', res.status, await res.text().catch(() => ''));
  }
}

export async function ensureUserInAirtable({ email, fullName = '', airtableBase }) {
  LOG('Called', { email: email ? `${email.slice(0, 6)}***` : null });

  if (!email) {
    console.error('[ensureUserInAirtable] Missing email');
    return { userAirtableId: null, companyId: null, companyDomain: null, companyName: null };
  }

  // Prefer server-side API (reliable, correct formula escaping, works if client env is stale)
  try {
    const apiRes = await fetch('/api/ensure-user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, fullName }),
    });
    if (apiRes.ok) {
      const result = await apiRes.json();
      LOG('Server API result', { userAirtableId: !!result.userAirtableId, created: result.created });
      return result;
    }
    LOG('Server API failed:', apiRes.status, await apiRes.text().catch(() => ''));
  } catch (apiErr) {
    LOG('Server API unavailable, falling back to client:', apiErr?.message);
  }

  if (!apiKey || !baseId || !usersTableId) {
    console.error('[ensureUserInAirtable] Missing config:', { hasEmail: !!email, hasApiKey: !!apiKey, hasBaseId: !!baseId, hasUsersTableId: !!usersTableId });
    return { userAirtableId: null, companyId: null, companyDomain: null, companyName: null };
  }

  const userEmailLower = email.toLowerCase().trim();
  const extractedDomain = extractDomain(userEmailLower);
  const isPersonal = isPersonalEmail(userEmailLower);

  // 1. Try to find existing user
  let existingUser = null;
  try {
    const formula = `LOWER({Email})='${String(userEmailLower).replace(/'/g, "\\'")}'`;
    const url = `https://api.airtable.com/v0/${baseId}/${usersTableId}?filterByFormula=${encodeURIComponent(formula)}`;
    LOG('GET existing, formula:', formula);
    const res = await fetchWithRetry(url, {
      headers: { Authorization: `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (res.ok && data.records?.length > 0) {
      existingUser = data.records[0];
      LOG('Found existing user:', existingUser.id);
    } else if (!res.ok) {
      LOG('Existing check failed:', res.status, JSON.stringify(data));
    } else {
      LOG('No existing user found');
    }
  } catch (err) {
    LOG('Existing check error:', err?.message);
  }

  if (existingUser) {
    let companyId = null, companyDomain = null, companyName = null;
    let linkedCompanyFields = null;
    let domainClientType = '';
    const companiesRaw = existingUser.fields?.['Companies'];
    const companies = !companiesRaw ? [] : typeof companiesRaw === 'string'
      ? companiesRaw.split(',').map((s) => s.trim()).filter(Boolean)
      : Array.isArray(companiesRaw) ? companiesRaw.map((c) => String(c).trim()).filter(Boolean) : [];
    if (companies.length > 0 && airtableBase) {
      companyId = companies[0];
      try {
        const company = await airtableBase('companies').find(companyId);
        linkedCompanyFields = company.fields || {};
        companyDomain = company.fields?.['Domain'] || null;
        companyName = company.fields?.['Name'] || null;
      } catch (e) {
        LOG('Error fetching company:', e?.message);
      }
    }
    if (!isPersonal && extractedDomain && airtableBase) {
      try {
        const safeDomain = extractedDomain.toLowerCase().replace(/'/g, "''");
        const companyRecords = await airtableBase('companies')
          .select({ filterByFormula: `OR({Domain}='${safeDomain}',{Domain}='www.${safeDomain}')`, maxRecords: 1 })
          .firstPage();
        if (companyRecords.length > 0) {
          const company = companyRecords[0];
          domainClientType = company.fields?.ClientType || '';
          if (!companyId) {
            companyId = company.id;
            companyDomain = company.fields?.['Domain'] || null;
            companyName = company.fields?.['Name'] || null;
          }
        }
      } catch (companyErr) {
        LOG('Existing user company lookup error:', companyErr?.message);
      }
    }
    const markPaid = clientTypeGrantsPaidAccess(domainClientType)
      || companyGrantsPaidAccess(linkedCompanyFields, extractedDomain);
    try {
      await markUserPaid(existingUser.id, existingUser.fields?.Paid, markPaid);
    } catch (paidErr) {
      LOG('Existing user paid update error:', paidErr?.message);
    }

    // Patch Domain / company link if user was created with minimal fields (common in OAuth flow)
    if (airtableBase && (extractedDomain || companyId)) {
      const patchFields = {};
      if (extractedDomain && !existingUser.fields?.Domain) {
        patchFields.Domain = extractedDomain.toLowerCase();
      }
      if (companyId && companies.length === 0) {
        patchFields['Companies'] = String(companyId);
        patchFields['Workspace Role'] = 'Member';
        patchFields['WorkspaceVerifiedUser'] = !isPersonal;
      }
      if (Object.keys(patchFields).length > 0) {
        try {
          await airtableBase('Users').update([{ id: existingUser.id, fields: patchFields }]);
        } catch (patchErr) {
          LOG('Existing user patch error:', patchErr?.message);
        }
      }
    }

    return {
      userAirtableId: existingUser.id,
      companyId,
      companyDomain: companyDomain || extractedDomain,
      companyName,
    };
  }

  // 2. Resolve company (for work emails)
  let companyId = null, companyDomain = extractedDomain, companyName = null;
  let markPaid = false;
  if (!isPersonal && extractedDomain && airtableBase) {
    try {
      const safeDomain = extractedDomain.toLowerCase().replace(/'/g, "''");
      const companyRecords = await airtableBase('companies')
        .select({ filterByFormula: `OR({Domain}='${safeDomain}',{Domain}='www.${safeDomain}')`, maxRecords: 1 })
        .firstPage();
      if (companyRecords.length > 0) {
        const company = companyRecords[0];
        companyId = company.id;
        companyDomain = company.fields?.['Domain'] || null;
        companyName = company.fields?.['Name'] || null;
        markPaid = clientTypeGrantsPaidAccess(company.fields?.ClientType);
        LOG('Found company:', companyId);
      } else {
        const newCompany = await airtableBase('companies').create({
          Domain: extractedDomain.toLowerCase(),
          Name: extractedDomain.split('.')[0].charAt(0).toUpperCase() + extractedDomain.split('.')[0].slice(1),
        });
        companyId = newCompany.id;
        companyName = newCompany.fields?.['Name'] || null;
        LOG('Created company:', companyId);
      }
    } catch (companyErr) {
      LOG('Company error:', companyErr?.message);
    }
  }

  // 3. Create user — work emails prefer full fields so Domain + company link are set
  try {
    const baseFields = { Email: userEmailLower };
    const fullFields = { ...baseFields, Domain: extractedDomain || '', IsPersonalEmail: isPersonal };
    if (companyId) {
      fullFields['Companies'] = String(companyId);
      fullFields['Workspace Role'] = 'Member';
      fullFields['WorkspaceVerifiedUser'] = !isPersonal;
    }
    if (markPaid) {
      fullFields.Paid = 'paid';
    }
    if (fullName?.trim()) {
      const parts = fullName.trim().split(/\s+/);
      fullFields['First Name'] = parts[0] || '';
      fullFields['Last Name'] = parts.slice(1).join(' ') || '';
    }

    const fieldAttempts = isPersonal
      ? [baseFields, { Email: userEmailLower, Domain: extractedDomain || '' }, fullFields]
      : [fullFields, { Email: userEmailLower, Domain: extractedDomain || '' }, baseFields];

    let createData;
    let lastErr;
    for (const fields of fieldAttempts) {
      try {
        createData = await createAirtableUser(fields);
        break;
      } catch (err) {
        lastErr = err;
        const msg = err?.message || '';
        if (!msg.includes('Unknown field') && !msg.includes('cannot accept')) {
          throw err;
        }
      }
    }
    if (!createData) {
      throw lastErr || new Error('Failed to create user');
    }

    const userAirtableId = createData.id;
    try {
      await markUserPaid(userAirtableId, createData.fields?.Paid, markPaid);
    } catch (paidErr) {
      LOG('New user paid update error:', paidErr?.message);
    }

    // Minimal create may succeed without Domain/Companies — patch if needed
    if (airtableBase && (extractedDomain || companyId)) {
      const patchFields = {};
      if (extractedDomain) patchFields.Domain = extractedDomain.toLowerCase();
      if (companyId) {
        patchFields['Companies'] = String(companyId);
        patchFields['Workspace Role'] = 'Member';
        patchFields['WorkspaceVerifiedUser'] = !isPersonal;
      }
      const createdCompanies = createData.fields?.['Companies'];
      const needsPatch = patchFields.Domain && !createData.fields?.Domain
        || (patchFields['Companies'] && !createdCompanies);
      if (needsPatch && Object.keys(patchFields).length > 0) {
        try {
          await airtableBase('Users').update([{ id: userAirtableId, fields: patchFields }]);
        } catch (patchErr) {
          LOG('New user patch error:', patchErr?.message);
        }
      }
    }

    LOG('Created user:', userAirtableId);

    return {
      userAirtableId,
      companyId,
      companyDomain,
      companyName,
    };
  } catch (createErr) {
    console.error('[ensureUserInAirtable] Create failed:', createErr);
    const is429 = createErr?.message?.includes('429');
    return { userAirtableId: null, companyId: null, companyDomain: null, companyName: null, errorReason: is429 ? 'rate_limit' : undefined };
  }
}
